// src/components/pos/ProductGrid.jsx
import React from 'react';
import { Package } from 'lucide-react';
import { useApp } from '../../context/AppContext';

// ProductGrid 只负责展示商品 + 点击回调，不再自己弹任何窗
// 点击后把「原始 product 对象」交给上层的 onProductClick 决定弹哪个弹窗
const ProductGrid = ({ products = [], onProductClick }) => {
  const { language } = useApp();

  const getProductName = (product) => {
    if (language === 'en') return product.name_en || product.name;
    if (language === 'km') return product.name_km || product.name;
    return product.name;
  };

  const getProductImage = (product) => {
    if (!product.image) return null;
    if (product.image.startsWith('http://') || product.image.startsWith('https://')) return product.image;
    return `http://47.79.252.120:5000${product.image}`;
  };

  if (!products || products.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400">
        <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
        <p>{language === 'en' ? 'No products found' : language === 'km' ? 'រកមិនឃើញទំនិញ' : '暂无商品'}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
      {products.map((product) => {
        const imageUrl = getProductImage(product);
        const productName = getProductName(product);
        const hasBox = product.box_quantity > 0;
        const hasSpecs = !!product.has_specs; // 后端返回的多规格标记

        return (
          <div
            key={product.id}
            onClick={() => onProductClick && onProductClick(product)}
            className="bg-white border rounded-xl p-3 hover:shadow-lg hover:border-blue-500 transition-all cursor-pointer group"
          >
            {/* 图片 */}
            <div className="aspect-square mb-3 bg-gray-100 rounded-lg overflow-hidden relative">
              {imageUrl ? (
                <img src={imageUrl} alt={productName}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  onError={e => { e.target.style.display = 'none'; }} />
              ) : null}
              <div className={`fallback-icon absolute inset-0 flex items-center justify-center ${imageUrl ? 'hidden' : 'flex'}`}>
                <Package className="w-12 h-12 text-gray-300" />
              </div>
              {product.stock <= product.min_stock && (
                <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                  {language === 'en' ? 'Low' : '低库存'}
                </div>
              )}
              {/* 箱装标签 */}
              {hasBox && (
                <div className="absolute top-2 left-2 bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded">
                  📦 {product.box_quantity}/箱
                </div>
              )}
              {/* 多规格标签 */}
              {hasSpecs && (
                <div className="absolute bottom-2 left-2 bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded">
                  多规格
                </div>
              )}
            </div>

            {/* 商品信息 */}
            <div className="space-y-0">
              <h3 className="font-medium text-xs text-gray-800 line-clamp-2 leading-tight">{productName}</h3>
              {language !== 'zh' && product.name && (
                <p className="text-xs text-gray-400 line-clamp-1">{product.name}</p>
              )}
              <div className="flex items-center justify-between mt-1">
                <span className="text-blue-600 font-bold text-sm">
                  ${parseFloat(product.sale_price).toFixed(2)}
                </span>
                <span className={`text-xs ${product.stock <= product.min_stock ? 'text-red-500' : 'text-gray-400'}`}>
                  库存 {product.stock}
                </span>
              </div>
              {product.barcode && (
                <div className="text-xs text-gray-300 truncate">{product.barcode}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProductGrid;